import { defineMessages, IntlShape } from 'react-intl';
import {
  IconWarehouse2,
  IconOrders,
  IconFinance,
  IconCustomer2,
  IconMail,
  IconStats,
  IconSettings,
  FailedOrder,
  IconMarketplace,
} from '../../../assets/svg/icons';
import routes from '../../../setup/consts/routes';
import { SideMenuItem } from '../../molecules/SideMenu/types';
import { SideMenuItemDefinition } from '../types';

const {
  DASHBOARD,
  WAREHOUSE,
  ORDERS,
  FINANCIALS,
  CUSTOMERS,
  MARKETPLACE,
  CONTACT_US,
  SETTINGS_SECTION,
  ENTITIES_AND_USERS,
  ENTITIES,
  BILLING_AND_PAYMENTS,
  ACCOUNT_DETAILS,
  FAILED_ORDERS,
} = routes;

const baseMenuMessages = defineMessages({
  [DASHBOARD]: {
    id: 'mainNavigation.dashboard',
    defaultMessage: 'Dashboard',
  },
  [WAREHOUSE]: {
    id: 'mainNavigation.warehouse',
    defaultMessage: 'Warehouse',
  },
  [ORDERS]: {
    id: 'mainNavigation.orders',
    defaultMessage: 'Orders',
  },
  [FINANCIALS]: {
    id: 'mainNavigation.financials',
    defaultMessage: 'Financials',
  },
  [CUSTOMERS]: {
    id: 'mainNavigation.customers',
    defaultMessage: 'Customers',
  },
  [FAILED_ORDERS]: {
    id: 'mainNavigation.failedOrders',
    defaultMessage: 'Failed Orders',
  },
});

// Marketplace is gated behind REACT_APP_ENABLE_MARKETPLACE so it can
// be turned on per environment (deployed dev, sbox) while staying out
// of prod. CRA inlines the literal at build time so the unused branch
// is dead-code eliminated when the flag is unset.
const devMenuMessages =
  process.env.REACT_APP_ENABLE_MARKETPLACE === '1'
    ? defineMessages({
        [MARKETPLACE]: {
          id: 'mainNavigation.marketplace',
          defaultMessage: 'Marketplace',
        },
      })
    : ({} as Record<string, never>);

const menuItemsIntl = { ...baseMenuMessages, ...devMenuMessages };

export const getMenuItems = ({
  isSandoogUser,
  isAdvancedUser,
}: {
  isSandoogUser: boolean;
  isAdvancedUser: boolean;
}): SideMenuItemDefinition[] => [
  {
    label: menuItemsIntl[DASHBOARD],
    to: DASHBOARD,
    icon: IconStats,
    key: '1',
  },
  {
    label: menuItemsIntl[ORDERS],
    to: ORDERS,
    icon: IconOrders,
    key: '3',
  },
  {
    label: menuItemsIntl[FAILED_ORDERS],
    to: FAILED_ORDERS,
    icon: FailedOrder,
    key: '4',
  },
  ...(isAdvancedUser
    ? [
        {
          label: menuItemsIntl[WAREHOUSE],
          to: WAREHOUSE,
          icon: IconWarehouse2,
          key: '2',
        },
      ]
    : []),
  {
    label: menuItemsIntl[CUSTOMERS],
    to: CUSTOMERS,
    icon: IconCustomer2,
    key: '6',
  },
  {
    label: menuItemsIntl[FINANCIALS],
    to: FINANCIALS,
    icon: IconFinance,
    key: '5',
  },
  ...(process.env.REACT_APP_ENABLE_MARKETPLACE === '1'
    ? [
        {
          label: menuItemsIntl[MARKETPLACE],
          to: MARKETPLACE,
          icon: IconMarketplace,
          key: '7',
        },
      ]
    : []),
];

export const sideMenuAdditionalItemsWithIcons = (
  intl: IntlShape
): SideMenuItem[] => [
  {
    label: intl.formatMessage({
      id: 'mainNavigation.settings',
      defaultMessage: 'Settings',
    }),
    to: ACCOUNT_DETAILS,
    key: 'settings',
    icon: IconSettings,
  },
  {
    label: intl.formatMessage({
      id: 'mainNavigation.contactUs',
      defaultMessage: 'Contact Us',
    }),
    to: CONTACT_US,
    key: '1',
    icon: IconMail,
  },
];

export enum UserViewPermissions {
  Dashboard = 'view:sdg-dashboard',
  Settings = 'view:sdg-settings',
  Orders = 'view:sdg-orders',
  Warehouse = 'view:sdg-warehouse',
  Financials = 'view:sdg-financials',
  Customer = 'view:sdg-customer',
  EntitiesUser = 'view:sdg-entities-user',
}

export const menuItemPermissionsMap: Record<string, UserViewPermissions> = {
  [DASHBOARD]: UserViewPermissions.Dashboard,
  [SETTINGS_SECTION]: UserViewPermissions.Settings,
  [ORDERS]: UserViewPermissions.Orders,
  [WAREHOUSE]: UserViewPermissions.Warehouse,
  [FINANCIALS]: UserViewPermissions.Financials,
  [CUSTOMERS]: UserViewPermissions.Customer,
  [ENTITIES_AND_USERS]: UserViewPermissions.EntitiesUser,
  [ENTITIES]: UserViewPermissions.EntitiesUser,
  [BILLING_AND_PAYMENTS]: UserViewPermissions.Settings,
  [ACCOUNT_DETAILS]: UserViewPermissions.Settings,
};
